import React, { useState, useRef, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Upload, Download, FileSpreadsheet, CheckCircle, XCircle, AlertTriangle, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/hooks/use-toast';
import { ALL_SYSTEM_TYPES, getSystemTypeLabel } from '@/lib/systemTypeConstants';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

interface CatalogoRow {
  tipo_sistema: string;
  categoria: string;
  tipo_equipo: string;
  marca: string;
  modelo: string;
}

interface ParsedRow {
  data: CatalogoRow;
  errors: string[];
  rowIndex: number;
}

type ImportStep = 'upload' | 'preview' | 'importing' | 'done';

const TEMPLATE_COLUMNS = ['tipo_sistema', 'categoria', 'tipo_equipo', 'marca', 'modelo'];

const COLUMN_LABELS: Record<string, string> = {
  tipo_sistema: 'Tipo Sistema *',
  categoria: 'Categoría *',
  tipo_equipo: 'Tipo Equipo *',
  marca: 'Marca *',
  modelo: 'Modelo *',
};

const VALID_CATEGORIAS = ['seguridad', 'pci'];

const normalizar = (txt: string) =>
  txt.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();

export const ImportarCatalogoDialog: React.FC<Props> = ({ open, onOpenChange, onSuccess }) => {
  const { perfil } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<ImportStep>('upload');
  const [parsedRows, setParsedRows] = useState<ParsedRow[]>([]);
  const [progress, setProgress] = useState(0);
  const [importResult, setImportResult] = useState({ tipos: 0, marcas: 0, modelos: 0, errors: 0 });

  const reset = useCallback(() => {
    setStep('upload');
    setParsedRows([]);
    setProgress(0);
    setImportResult({ tipos: 0, marcas: 0, modelos: 0, errors: 0 });
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const handleOpenChange = (value: boolean) => {
    if (!value) reset();
    onOpenChange(value);
  };

  const downloadTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([
      TEMPLATE_COLUMNS.map(c => COLUMN_LABELS[c]),
      ['intrusión', 'seguridad', 'Central', 'Ajax', 'Hub 2'],
      ['intrusión', 'seguridad', 'Detector', 'Ajax', 'MotionProtect'],
      ['intrusión', 'seguridad', 'Teclado', 'Ajax', 'KeyPad Plus'],
      ['CCTV', 'seguridad', 'Cámara', 'HikVision', 'DS-2CD2143G2-I'],
      ['CCTV', 'seguridad', 'Grabador', 'HikVision', 'DS-7608NI-K2'],
      ['extintores', 'pci', 'Extintor Polvo', 'Firex', 'P6'],
    ]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Catálogo Equipos');
    ws['!cols'] = TEMPLATE_COLUMNS.map(() => ({ wch: 22 }));
    XLSX.writeFile(wb, 'plantilla_catalogo_equipos.xlsx');
  };

  const parseFile = async (file: File) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json<Record<string, any>>(sheet, { defval: '' });

        if (jsonData.length === 0) {
          toast({ title: 'Archivo vacío', description: 'El archivo no contiene datos.', variant: 'destructive' });
          return;
        }

        const validTypes = new Set(Object.keys(ALL_SYSTEM_TYPES).map(t => normalizar(t)));

        const reverseMap: Record<string, string> = {};
        for (const key of TEMPLATE_COLUMNS) {
          reverseMap[(COLUMN_LABELS[key]).toLowerCase().replace(/\s*\*\s*/g, '').trim()] = key;
          reverseMap[key.toLowerCase()] = key;
        }

        const rows: ParsedRow[] = jsonData.map((row, idx) => {
          const mapped: Record<string, string> = {};
          for (const [header, value] of Object.entries(row)) {
            const normalizedHeader = header.toLowerCase().replace(/\s*\*\s*/g, '').trim();
            const matchedKey = reverseMap[normalizedHeader];
            if (matchedKey) mapped[matchedKey] = String(value || '').trim();
          }

          const errors: string[] = [];
          if (!mapped.tipo_sistema) errors.push('Falta tipo sistema');
          else if (!validTypes.has(normalizar(mapped.tipo_sistema))) errors.push('Tipo sistema inválido');

          if (!mapped.categoria) errors.push('Falta categoría');
          else if (!VALID_CATEGORIAS.includes(normalizar(mapped.categoria))) errors.push('Categoría inválida');

          if (!mapped.tipo_equipo) errors.push('Falta tipo equipo');
          if (!mapped.marca) errors.push('Falta marca');
          if (!mapped.modelo) errors.push('Falta modelo');

          return { data: mapped as unknown as CatalogoRow, errors, rowIndex: idx + 2 };
        });

        setParsedRows(rows);
        setStep('preview');
      } catch {
        toast({ title: 'Error al leer archivo', description: 'No se pudo parsear el archivo.', variant: 'destructive' });
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) parseFile(file);
  };

  const validRows = parsedRows.filter(r => r.errors.length === 0);
  const invalidRows = parsedRows.filter(r => r.errors.length > 0);

  const handleImport = async () => {
    if (!perfil?.empresa_id) return;
    setStep('importing');

    let tiposCreados = 0;
    let marcasCreadas = 0;
    let modelosCreados = 0;
    let errors = 0;
    const totalOps = validRows.length;

    // Load existing catalog to avoid duplicates
    const [tiposRes, marcasRes, modelosRes] = await Promise.all([
      supabase.from('tipos_equipo').select('id, nombre, sistema_tipo'),
      supabase.from('marcas_equipo').select('id, nombre, sistema_tipo'),
      supabase.from('modelos_equipo').select('id, nombre, marca_id, sistema_tipo, tipo_equipo'),
    ]);

    // Build lookup maps: key -> id
    const tiposMap = new Map<string, string>();
    (tiposRes.data || []).forEach(t => {
      tiposMap.set(`${normalizar(t.sistema_tipo)}|${normalizar(t.nombre)}`, t.id);
    });

    const marcasMap = new Map<string, string>();
    (marcasRes.data || []).forEach(m => {
      marcasMap.set(`${normalizar(m.sistema_tipo)}|${normalizar(m.nombre)}`, m.id);
    });

    const modelosSet = new Set<string>();
    (modelosRes.data || []).forEach(m => {
      modelosSet.add(`${m.marca_id}|${normalizar(m.nombre)}|${normalizar(m.tipo_equipo || '')}`);
    });

    for (let i = 0; i < validRows.length; i++) {
      const row = validRows[i];
      const sistemaTipo = normalizar(row.data.tipo_sistema);
      const categoria = normalizar(row.data.categoria) as 'seguridad' | 'pci';
      const tipoEquipoNombre = row.data.tipo_equipo.trim();
      const marcaNombre = row.data.marca.trim();
      const modeloNombre = row.data.modelo.trim();

      try {
        // 1. Ensure tipo_equipo exists
        const tipoKey = `${sistemaTipo}|${normalizar(tipoEquipoNombre)}`;
        if (!tiposMap.has(tipoKey)) {
          const { data: newTipo, error } = await supabase
            .from('tipos_equipo')
            .insert({ nombre: tipoEquipoNombre, sistema_tipo: sistemaTipo, categoria, empresa_id: perfil.empresa_id })
            .select('id')
            .single();
          if (error) { errors++; continue; }
          tiposMap.set(tipoKey, newTipo.id);
          tiposCreados++;
        }

        // 2. Ensure marca exists
        const marcaKey = `${sistemaTipo}|${normalizar(marcaNombre)}`;
        let marcaId = marcasMap.get(marcaKey);
        if (!marcaId) {
          const { data: newMarca, error } = await supabase
            .from('marcas_equipo')
            .insert({ nombre: marcaNombre, sistema_tipo: sistemaTipo, categoria, empresa_id: perfil.empresa_id })
            .select('id')
            .single();
          if (error) { errors++; continue; }
          marcaId = newMarca.id;
          marcasMap.set(marcaKey, marcaId);
          marcasCreadas++;
        }

        // 3. Ensure modelo exists
        const modeloKey = `${marcaId}|${normalizar(modeloNombre)}|${normalizar(tipoEquipoNombre)}`;
        if (!modelosSet.has(modeloKey)) {
          const { error } = await supabase
            .from('modelos_equipo')
            .insert({
              nombre: modeloNombre,
              marca_id: marcaId,
              sistema_tipo: sistemaTipo,
              tipo_equipo: tipoEquipoNombre,
              categoria,
              empresa_id: perfil.empresa_id,
            });
          if (error) { errors++; } else {
            modelosCreados++;
            modelosSet.add(modeloKey);
          }
        }
      } catch {
        errors++;
      }

      setProgress(Math.round(((i + 1) / totalOps) * 100));
    }

    setImportResult({ tipos: tiposCreados, marcas: marcasCreadas, modelos: modelosCreados, errors: errors + invalidRows.length });
    setStep('done');
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[750px] max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5" />
            Importar Catálogo de Equipos
          </DialogTitle>
          <DialogDescription>
            Importa tipos de equipo, marcas y modelos desde un archivo Excel o CSV.
          </DialogDescription>
        </DialogHeader>

        {step === 'upload' && (
          <div className="space-y-6 py-4">
            <div className="flex justify-center">
              <Button variant="outline" onClick={downloadTemplate} className="gap-2">
                <Download className="h-4 w-4" />Descargar plantilla Excel
              </Button>
            </div>
            <div className="bg-muted/50 border rounded-lg p-3 text-sm text-muted-foreground">
              <p className="font-medium text-foreground mb-1">ℹ️ Cada fila define un modelo con su tipo, marca y sistema:</p>
              <ul className="list-disc list-inside space-y-1">
                <li><strong>Tipo Sistema:</strong> intrusión, CCTV, accesos, extintores...</li>
                <li><strong>Categoría:</strong> seguridad o pci</li>
                <li><strong>Tipo Equipo:</strong> Central, Detector, Teclado, Cámara...</li>
                <li><strong>Marca y Modelo:</strong> Se crean automáticamente si no existen</li>
              </ul>
            </div>
            <div
              className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-primary/50 hover:bg-muted/50 transition-colors"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
              <p className="font-medium">Haz clic para seleccionar un archivo</p>
              <p className="text-sm text-muted-foreground mt-1">Formatos: .xlsx, .xls, .csv</p>
              <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handleFileChange} />
            </div>
          </div>
        )}

        {step === 'preview' && (
          <div className="space-y-4 flex-1 min-h-0">
            <div className="flex items-center gap-3 flex-wrap">
              {validRows.length > 0 && <Badge variant="default" className="gap-1"><CheckCircle className="h-3 w-3" />{validRows.length} válidos</Badge>}
              {invalidRows.length > 0 && <Badge variant="destructive" className="gap-1"><XCircle className="h-3 w-3" />{invalidRows.length} con errores</Badge>}
              <span className="text-sm text-muted-foreground">Total: {parsedRows.length} filas</span>
            </div>
            <ScrollArea className="h-[350px] border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">#</TableHead>
                    <TableHead>Sistema</TableHead>
                    <TableHead>Tipo Equipo</TableHead>
                    <TableHead>Marca</TableHead>
                    <TableHead>Modelo</TableHead>
                    <TableHead className="w-28">Estado</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {parsedRows.map((row) => (
                    <TableRow key={row.rowIndex} className={row.errors.length > 0 ? 'bg-destructive/10' : ''}>
                      <TableCell className="text-muted-foreground text-xs">{row.rowIndex}</TableCell>
                      <TableCell className="text-sm">{row.data.tipo_sistema || '—'}</TableCell>
                      <TableCell className="text-sm">{row.data.tipo_equipo || '—'}</TableCell>
                      <TableCell className="text-sm">{row.data.marca || '—'}</TableCell>
                      <TableCell className="font-medium text-sm">{row.data.modelo || '—'}</TableCell>
                      <TableCell>
                        {row.errors.length > 0 ? (
                          <span className="text-destructive text-xs flex items-center gap-1">
                            <AlertTriangle className="h-3 w-3" />{row.errors[0]}
                          </span>
                        ) : (
                          <Badge variant="outline" className="text-xs gap-1 border-green-500 text-green-600">
                            <CheckCircle className="h-3 w-3" />OK
                          </Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>
            <div className="flex justify-between items-center pt-2">
              <Button variant="outline" onClick={reset}>Cancelar</Button>
              <Button onClick={handleImport} disabled={validRows.length === 0} className="gap-2">
                <Upload className="h-4 w-4" />Importar {validRows.length} registros
              </Button>
            </div>
          </div>
        )}

        {step === 'importing' && (
          <div className="py-8 space-y-4">
            <div className="flex items-center justify-center gap-2">
              <Loader2 className="h-5 w-5 animate-spin" /><span className="font-medium">Importando catálogo...</span>
            </div>
            <Progress value={progress} className="h-2" />
            <p className="text-center text-sm text-muted-foreground">{progress}%</p>
          </div>
        )}

        {step === 'done' && (
          <div className="py-8 space-y-4 text-center">
            <CheckCircle className="h-12 w-12 mx-auto text-green-500" />
            <h3 className="text-lg font-semibold">Importación completada</h3>
            <div className="flex justify-center gap-3 flex-wrap">
              {importResult.tipos > 0 && <Badge variant="secondary" className="text-sm gap-1">{importResult.tipos} tipos creados</Badge>}
              {importResult.marcas > 0 && <Badge variant="secondary" className="text-sm gap-1">{importResult.marcas} marcas creadas</Badge>}
              {importResult.modelos > 0 && <Badge variant="default" className="text-sm gap-1"><CheckCircle className="h-3 w-3" />{importResult.modelos} modelos creados</Badge>}
              {importResult.errors > 0 && <Badge variant="destructive" className="text-sm gap-1"><XCircle className="h-3 w-3" />{importResult.errors} con errores</Badge>}
            </div>
            <Button onClick={() => { handleOpenChange(false); onSuccess(); }} className="mt-4">Cerrar</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
